import React, { useCallback } from 'react'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { CaptionSM, HeadingSM, HeadingXL, ParagraphMD, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { BoundedImage } from '@/molecules/BoundedImage'
import { MediaCard } from '@/molecules/MediaCard'
import { ProgressOverlay } from '@/molecules/MediaCard/ProgressOverlay'
import { ProjectMeta, VideoMetaData } from '@/services/ProjectsService'
import { Media, useFormatDuration } from '@/utils/EpisodeUtil'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export interface WatchProjectBonusContentViewProps {
  bonusVideos: VideoMetaData[]
  movieSpecialMedias: Media[]
  projectMeta: ProjectMeta
  userHasEarlyAccess: boolean
  hasWatchableReasons: boolean
}

export const WatchProjectBonusContentView: React.FC<WatchProjectBonusContentViewProps> = ({
  bonusVideos,
  movieSpecialMedias,
  projectMeta,
  userHasEarlyAccess,
  hasWatchableReasons,
}) => {
  const { t } = useTranslate('watch')
  const track = useSafeTrack()
  const formatDuration = useFormatDuration(t)

  const hasBonusContent =
    (bonusVideos && bonusVideos.length > 0) || (movieSpecialMedias && movieSpecialMedias.length > 0)
  const titleLogo = projectMeta?.title && 'logo' in projectMeta.title ? projectMeta.title.logo : null
  const logoUrl =
    titleLogo && typeof titleLogo === 'object' && titleLogo !== null && 'cloudinaryPath' in titleLogo
      ? (titleLogo.cloudinaryPath as string)
      : projectMeta?.logoCloudinaryPath
  const projectName = projectMeta?.name
  const projectSlug = projectMeta?.slug

  const handleBonusVideoClick = useCallback(
    (video: VideoMetaData) => {
      track('Watch Bonus Content Page Bonus Video Clicked', {
        program: projectSlug,
        videoId: video.guid,
        title: video.subtitle,
      })
    },
    [track, projectSlug],
  )

  const handleCtaClick = useCallback(() => {
    track('Watch Bonus Content Page CTA Clicked', {
      program: projectSlug,
      title: projectName,
    })
  }, [track, projectSlug, projectName])

  return (
    <div className="min-h-screen bg-core-gray-950 pb-20 pt-28 text-white">
      <PaddedContainer fluid>
        <div className="mb-12">
          {logoUrl && (
            <BoundedImage
              className="mb-2 flex items-center justify-center md:justify-start lg:mb-4"
              alt={projectMeta?.name}
              path={logoUrl}
              width={600}
              height={104}
            />
          )}
          <HeadingXL weight="bold" className="mb-4">
            {t('bonusContent', 'Bonus Content')}
          </HeadingXL>
          <ParagraphMD color="core-gray-500">
            <Translate t={t} i18nKey="watchProjectBonusContentDescription" values={{ projectName }}>
              Watch behind-the-scenes clips, bonus videos, and exclusive previews from <i>{{ projectName }}</i>.
              Discover how your favorite stories came to life.
            </Translate>
          </ParagraphMD>
        </div>

        <When condition={!hasBonusContent}>
          <div className="py-20 text-left">
            <HeadingSM weight="semibold" className="mb-4">
              {t('noBonusContentAvailable', 'No bonus content available at this time')}
            </HeadingSM>
            <ParagraphSM color="core-gray-400">
              {t('checkBackLaterBonusContent', 'Check back later for new bonus content')}
            </ParagraphSM>
          </div>
        </When>

        <When condition={hasBonusContent}>
          <LazyHydrate whenVisible>
            <div className="flex flex-col gap-y-10 md:gap-y-6 lg:gap-y-10">
              {bonusVideos?.map((video) => {
                const durationLabel =
                  typeof video.source.duration === 'number' ? formatDuration(video.source.duration) : undefined
                const href = `/watch/${projectSlug}/videos/${video.page}/${video.guid}`
                let progress = 0

                if (video?.watchPosition?.position && video.source?.duration) {
                  progress = (video?.watchPosition?.position / video?.source.duration) * 100
                }
                return (
                  <div key={video.guid} className="flex w-full flex-row items-start gap-4">
                    <div className="shrink-0 snap-center snap-always">
                      <InternalLink
                        href={href}
                        className="relative cursor-pointer"
                        onClick={() => handleBonusVideoClick(video)}
                      >
                        <Image
                          alt={`${video.title} Bonus Content Thumbnail`}
                          src={video.posterLandscapeCloudinaryPath || ''}
                          className="h-full max-h-[82px] w-full max-w-[146px] rounded-lg lg:max-h-[177px] lg:max-w-[316px]"
                          width={316}
                          height={177}
                        />
                        <PlayButtonThumbnailOverlay />
                        <ProgressOverlay progress={progress} />
                      </InternalLink>
                    </div>
                    <div className="space-between flex h-full w-full flex-row items-center gap-x-4 lg:items-start lg:gap-x-0">
                      <div className="flex grow-1 flex-col gap-[6px]">
                        <CaptionSM color="core-gray-500" className="md:photon-caption-md lg:photon-caption-lg">
                          {t('bonus', 'Bonus')}
                        </CaptionSM>
                        <CaptionSM weight="bold" className="lg:photon-title-md line-clamp-2 lg:line-clamp-4">
                          {video.subtitle}
                        </CaptionSM>
                        {durationLabel && (
                          <CaptionSM color="core-gray-500" className="md:photon-paragraph-md">
                            {durationLabel}
                          </CaptionSM>
                        )}
                      </div>
                      <a className="lg:hidden" href={href}>
                        <CaretRightIcon size={24} color="white" />
                      </a>
                    </div>
                  </div>
                )
              })}
              {movieSpecialMedias.map((media, i) => {
                if (userHasEarlyAccess && media?.watchableType === 'SNEAK_PEEK') {
                  return null
                }
                return (
                  <MediaCard
                    className="relative flex min-h-[100px] w-full shrink-0 snap-center snap-always flex-row items-start gap-4"
                    thumbnailClassName="h-full w-full min-w-[180px] md:min-w-[256px] lg:min-w-[300px]"
                    {...media}
                    key={media.guid}
                    imgPriority={i <= 4}
                    includeHoverScale={false}
                    userHasEarlyAccess={userHasEarlyAccess}
                    hasWatchableReasons={hasWatchableReasons}
                    watchableAvailabilityStatus={media?.watchableAvailabilityStatus ?? null}
                    theaterEpisodeCard
                  />
                )
              })}
            </div>
          </LazyHydrate>
        </When>

        <LinkButton
          onClick={handleCtaClick}
          href={`${paths.watch.index}/${projectSlug}`}
          className="mt-12 w-full border-transparent bg-core-gray-800 hover:bg-core-gray-800/50 md:w-fit"
          variant={'core-gray-700'}
        >
          {t('watchProject', 'Watch {{ projectName }}', { projectName })}
        </LinkButton>
      </PaddedContainer>
    </div>
  )
}
